/**
 * DesktopProfileEdit — "Edit profile" sub-view for the desktop Account screen.
 *
 * Rendered at /account/profile. The shell's AppHeader shows a Back button
 * (back to /account). No back button needed here.
 *
 * Logic copied from Account.jsx's ProfileEditor — first name, last name,
 * company name. PATCH ${API}/account, then refresh + navigate back.
 */
import { useState, useEffect } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getIdToken } from '@/lib/auth';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

export default function DesktopProfileEdit() {
  const navigate = useNavigate();
  const { account, refreshAccount } = useAuth();

  const initialFirst   = account?.firstName   || '';
  const initialLast    = account?.lastName    || '';
  const initialCompany = account?.companyName || '';

  const [firstName,   setFirstName]   = useState(initialFirst);
  const [lastName,    setLastName]    = useState(initialLast);
  const [companyName, setCompanyName] = useState(initialCompany);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(false);

  // Re-seed when account loads (may arrive after mount)
  useEffect(() => {
    setFirstName(account?.firstName   || '');
    setLastName(account?.lastName    || '');
    setCompanyName(account?.companyName || '');
  }, [account?.firstName, account?.lastName, account?.companyName]);

  const trimFirst   = firstName.trim();
  const trimLast    = lastName.trim();
  const trimCompany = companyName.trim();

  const dirty =
    trimFirst   !== (initialFirst   || '').trim() ||
    trimLast    !== (initialLast    || '').trim() ||
    trimCompany !== (initialCompany || '').trim();

  const canSave = !!trimFirst && dirty && !saving;

  async function handleSave(e) {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/account`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          first_name:   trimFirst,
          last_name:    trimLast,
          company_name: trimCompany,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data?.message || 'Could not save profile.');
      }
      setSuccess(true);
      await refreshAccount();
      // Brief success flash then navigate back
      setTimeout(() => navigate('/account'), 900);
    } catch (err) {
      setError(err.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  }

  // ── Styles (--ds-* tokens) ───────────────────────────────────────────────
  const cardStyle = {
    background: 'var(--ds-surface, #fff)',
    border: '1px solid var(--ds-border, #EAEBF0)',
    borderRadius: 'var(--ds-radius, 12px)',
    boxShadow: 'var(--ds-shadow-card, 0 1px 2px rgba(20,22,48,.05))',
    padding: '24px',
  };
  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--ds-ink-2, #565869)',
    marginBottom: '6px',
  };
  const inputStyle = {
    width: '100%',
    padding: '9px 12px',
    border: '1px solid var(--ds-border-2, #D4D7E3)',
    borderRadius: '8px',
    fontFamily: 'inherit',
    fontSize: '13.5px',
    color: 'var(--ds-ink, #191A24)',
    background: 'var(--ds-surface, #fff)',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '24px', maxWidth: '520px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 4px', color: 'var(--ds-ink, #191A24)' }}>
          Edit profile
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--ds-ink-2, #565869)', margin: 0 }}>
          Update your name and company.
        </p>
      </div>

      <div style={cardStyle}>
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label htmlFor="dp-first" style={labelStyle}>
                First name <span style={{ color: 'var(--ds-red, #B42318)' }}>*</span>
              </label>
              <input
                id="dp-first"
                type="text"
                required
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                maxLength={100}
                disabled={saving || success}
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="dp-last" style={labelStyle}>Last name</label>
              <input
                id="dp-last"
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                maxLength={100}
                disabled={saving || success}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="dp-company" style={labelStyle}>Company</label>
            <input
              id="dp-company"
              type="text"
              autoComplete="organization"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Company (optional)"
              maxLength={200}
              disabled={saving || success}
              style={inputStyle}
            />
          </div>

          {error && (
            <p role="alert" style={{ fontSize: '13px', color: 'var(--ds-red, #B42318)', marginBottom: '12px' }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              onClick={() => navigate('/account')}
              disabled={saving}
              style={{
                height: '36px', padding: '0 16px',
                borderRadius: '9px',
                background: 'var(--ds-surface, #fff)',
                border: '1px solid var(--ds-border-2, #D4D7E3)',
                fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                color: 'var(--ds-ink-2, #565869)', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSave}
              style={{
                height: '36px', padding: '0 18px',
                borderRadius: '9px',
                background: success ? 'var(--ds-green-bright, #16A34A)' : 'var(--ds-indigo, #4F46E5)',
                border: 'none',
                fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                color: 'var(--ds-content, #fff)', cursor: canSave ? 'pointer' : 'default',
                opacity: canSave ? 1 : 0.5,
                display: 'inline-flex', alignItems: 'center', gap: '7px',
              }}
            >
              {success ? (
                <><CheckCircle size={14} /> Saved</>
              ) : saving ? (
                <><Loader2 size={14} className="animate-spin" /> Saving&hellip;</>
              ) : (
                'Save changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
